import React from 'react';
import NextImage from 'next/image';
import { cn } from '../../utils/cn';
import type { ImageProps } from './image.types';

/**
 * Image component - Wrapper around Next.js Image for optimization
 * Provides automatic image optimization, lazy loading, and responsive images
 */
const Image: React.FC<ImageProps> = ({
    src,
    alt,
    width,
    height,
    objectFit = 'cover',
    borderRadius,
    className,
    loading = 'lazy',
    quality = 75,
    priority = false,
    placeholder = 'empty',
    fill = false,
    style,
    showIf = true,
    ...props
}) => {
    if (!showIf) return null;

    const imageStyle: React.CSSProperties = {
        objectFit,
        pointerEvents: 'none',
        ...(borderRadius && {
            borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius,
        }),
        ...style,
    };

    // If fill is true, width and height are not needed
    if (fill) {
        return (
            <NextImage
                src={src}
                alt={alt}
                fill
                quality={quality}
                priority={priority}
                placeholder={placeholder}
                loading={priority ? undefined : loading}
                className={cn('inline-block', className)}
                style={imageStyle}
                {...props}
            />
        );
    }

    // For fixed dimensions, width and height are required
    return (
        <NextImage
            src={src}
            alt={alt}
            width={width || 0}
            height={height || 0}
            quality={quality}
            priority={priority}
            placeholder={placeholder}
            loading={priority ? undefined : loading}
            className={cn('inline-block', className)}
            style={imageStyle}
            {...props}
        />
    );
};

export default Image;
